import React, { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { isSafari } from 'shared/utils/is-safari'

interface Option {
  value: string
  label: React.ReactNode
}

interface Props {
  options: Option[]
  onChange: (value: number) => void
}

const THREE_NEXT_ELEMENTS_HEIGHT = 80

const scrollToItem = (index: number, listRef: React.RefObject<HTMLUListElement>): void => {
  const list = listRef.current
  if (list) {
    const item = list.children[index] as HTMLElement
    const itemTop = item.offsetTop
    const itemBottom = itemTop + item.clientHeight
    const listHeight = list.clientHeight

    if (itemTop < list.scrollTop) {
      list.scrollTop = itemTop - THREE_NEXT_ELEMENTS_HEIGHT
    } else if (itemBottom > list.scrollTop + listHeight) {
      list.scrollTop = itemBottom + THREE_NEXT_ELEMENTS_HEIGHT - listHeight
    }
  }
}

const useDropdownKeyboardNavigation = ({
  optionsLength,
  activeIndex,
  setActiveIndex,
  selectOption,
  options,
  listRef,
}: {
  optionsLength: number
  activeIndex: number
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>
  selectOption: (value: number | boolean) => void
  options: Option[]
  listRef: React.RefObject<HTMLUListElement>
}): void => {
  const keyDownCallback = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Up':
        case 'ArrowUp': {
          e.preventDefault()
          setActiveIndex(prev => {
            const index = prev > 0 ? prev - 1 : optionsLength - 1
            scrollToItem(index, listRef)
            return index
          })
          return
        }
        case 'Down':
        case 'ArrowDown': {
          e.preventDefault()
          setActiveIndex(prev => {
            const index = prev < optionsLength - 1 ? prev + 1 : 0
            scrollToItem(index, listRef)
            return index
          })
          return
        }
        case 'Enter':
        case ' ': {
          e.preventDefault()
          selectOption(Number(options[activeIndex].value))
          return
        }
        case 'Escape': {
          e.preventDefault()
          selectOption(false)
          return
        }
        case 'Home': {
          e.preventDefault()
          setActiveIndex(0)
          scrollToItem(0, listRef)
          return
        }
        case 'End': {
          e.preventDefault()
          setActiveIndex(optionsLength - 1)
          scrollToItem(optionsLength - 1, listRef)
          return
        }
      }
    },
    [activeIndex, optionsLength, selectOption, options, listRef],
  )

  useEffect(() => {
    document.addEventListener('keydown', keyDownCallback)
    return () => document.removeEventListener('keydown', keyDownCallback)
  }, [keyDownCallback])
}

const useOpenDropdownHandlers = ({
  setIsDropdownOpen,
}: {
  setIsDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>
}): void => {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown', 'Enter', ' '].includes(e.key)) {
        e.preventDefault()
        setIsDropdownOpen(true)
      }
    },
    [setIsDropdownOpen],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])
}

const useAccessibleDropdown = ({
  options,
  onChange,
}: {
  options: Option[]
  onChange: (value: number) => void
}): {
  isDropdownOpen: boolean
  setIsDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>
  activeIndex: number
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>
  selectOption: (value: number | boolean) => void
  listRef: React.RefObject<HTMLUListElement>
} => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const listRef = useRef<HTMLUListElement>(null)

  const selectOption = useCallback(
    (value: number | boolean) => {
      if (typeof value === 'number') onChange(value)
      setIsDropdownOpen(false)
    },
    [onChange],
  )

  useDropdownKeyboardNavigation({
    optionsLength: options.length,
    activeIndex,
    setActiveIndex,
    selectOption,
    options,
    listRef,
  })

  useOpenDropdownHandlers({ setIsDropdownOpen })

  useEffect(() => {
    if (isDropdownOpen && listRef.current && isSafari()) {
      requestAnimationFrame(() => listRef.current?.focus())
    } else if (listRef.current && isSafari()) {
      requestAnimationFrame(() => (listRef.current?.previousSibling as HTMLElement)?.focus())
    }
  }, [isDropdownOpen])

  return {
    isDropdownOpen,
    setIsDropdownOpen,
    activeIndex,
    setActiveIndex,
    selectOption,
    listRef,
  }
}

export const ListBox = ({ options, onChange }: Props): ReactNode => {
  const { setIsDropdownOpen, activeIndex, setActiveIndex, selectOption, listRef } =
    useAccessibleDropdown({
      options,
      onChange,
    })

  useEffect(() => {
    setIsDropdownOpen(true)
  }, [setIsDropdownOpen])

  return (
    <ul
      ref={listRef}
      role="listbox"
      className="m-0 box-border max-h-[inherit] w-full origin-top list-none overflow-y-auto bg-background-secondary py-2 text-[1.2em] text-text-secondary transition-transform duration-200"
    >
      {options.map(({ label: Label, value: optionValue }, index) => (
        <li
          key={optionValue}
          id={`select_option_${optionValue}`}
          role="option"
          aria-selected={index === activeIndex}
          onMouseOver={() => setActiveIndex(index)}
          onClick={() => selectOption(Number(optionValue))}
          className="aria-selected:bg-text-primary aria-selected:text-background-secondary"
        >
          <label className="box-border flex w-full cursor-pointer px-3 py-1">
            <input
              type="radio"
              name="select_radio"
              value={optionValue}
              onChange={() => selectOption(index)}
              className="m-0 size-0 overflow-hidden p-0 opacity-0"
            />
            <span>{Label}</span>
          </label>
        </li>
      ))}
    </ul>
  )
}
